/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {LoadGeofenceEventsParams, ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageState} from '@/types/pages';
import {
  BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_ERROR,
  BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_REQUEST,
  BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_RESPONSE,
  GEOFENCE_EVENTS_GRID_REFRESHED,
  GET_GEOFENCE_EVENTS_ACKNOWLEDGE_ERROR,
  GET_GEOFENCE_EVENTS_ACKNOWLEDGE_REQUEST,
  GET_GEOFENCE_EVENTS_ACKNOWLEDGE_RESPONSE,
  GET_GEOFENCE_EVENTS_ERROR,
  GET_GEOFENCE_EVENTS_REQUEST,
  GET_GEOFENCE_EVENTS_RESPONSE,
  INITIALIZE_GEOFENCE_EVENTS,
  REFRESH_GEOFENCE_EVENTS_DATA,
  SET_GEOFENCE_EVENTS_GRID_COLUMNS,
  SET_GEOFENCE_GRID_FILTER_MODEL,
  UPDATE_GEOFENCE_EVENTS_GRID_COLUMNS
} from '../actions/geofence-events';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

export interface GeofenceEventsState extends PageState {
  geofenceEvents: {
    error: ServiceError;
    loading: boolean;
    result: LoadGeofenceEventsParams | null;
    acknowledgedError: ServiceError;
    refresh: boolean;
    eventCount: number;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {}
  },
  bulkAcknowledge: {
    isEditing: boolean,
    selected: LoadGeofenceEventsParams,
    loading: boolean,
    error: ServiceError,
    result: LoadGeofenceEventsParams | null;
  },
};

// defines the user preference key for the geofence events page ...
const GEOFENCE_PREFERENCE = 'geofence-events';

// defines the initial state for the reducer ...
export const initialState: GeofenceEventsState = {
  isInitialized  : false,
  geofenceEvents : {
    error             : null, //when geofence events grid fails to load data..error is tracked here.
    acknowledgedError : null, //response tracked here when Acknowledge button in grid fails to acknowledge
    loading           : false,
    result            : null,
    refresh           : false,
    eventCount        : null
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {
      acknowledge : {
        filterType : 'text',
        type       : 'equals',
        filter     : 'No'
      }
    }
  },
  bulkAcknowledge : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null, // response tracked here when bulk acknowledge selector throws error.
    result    : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_GEOFENCE_EVENTS] : draft => {
    draft.isInitialized  = true;
    const geofenceEventsPreference  = getGridColumnState(GEOFENCE_PREFERENCE, true);
    draft.grid.columnState          = geofenceEventsPreference;
    draft.grid.preferedColumnState  = geofenceEventsPreference;
  },
  [SET_GEOFENCE_EVENTS_GRID_COLUMNS] : ({grid}: GeofenceEventsState, {columnState}) => {
    onSetGridColumns({
      preference : GEOFENCE_PREFERENCE,
      isLoading  : false,
      grid
    }, columnState);
  },
  [UPDATE_GEOFENCE_EVENTS_GRID_COLUMNS] : ({grid}: GeofenceEventsState, {columnState}) => {
    onUpdateGridColumns({
      preference : GEOFENCE_PREFERENCE,
      grid
    }, columnState);
  },
  [GET_GEOFENCE_EVENTS_REQUEST] : ({geofenceEvents}: GeofenceEventsState) => {
    geofenceEvents.loading = true;
  },
  [GET_GEOFENCE_EVENTS_RESPONSE] : ({geofenceEvents}: GeofenceEventsState, {count}) => {
    geofenceEvents.loading    = false;
    geofenceEvents.eventCount = count;
  },
  [GET_GEOFENCE_EVENTS_ERROR] : ({geofenceEvents}: GeofenceEventsState, {error}) => {
    geofenceEvents.loading = false;
    geofenceEvents.error   = error;
  },
  [REFRESH_GEOFENCE_EVENTS_DATA] : ({geofenceEvents}: GeofenceEventsState, {val}) => {
    geofenceEvents.refresh = val;
  },
  [GEOFENCE_EVENTS_GRID_REFRESHED] : ({geofenceEvents, bulkAcknowledge}: GeofenceEventsState) => {
    geofenceEvents.result  = null;
    bulkAcknowledge.result = null;
  },
  [GET_GEOFENCE_EVENTS_ACKNOWLEDGE_REQUEST] : ({geofenceEvents}: GeofenceEventsState) => {
    geofenceEvents.loading = true;
  },
  [GET_GEOFENCE_EVENTS_ACKNOWLEDGE_RESPONSE] : ({geofenceEvents}: GeofenceEventsState, {asset}) => {
    geofenceEvents.loading = false;
    geofenceEvents.result  = asset;
  },
  [GET_GEOFENCE_EVENTS_ACKNOWLEDGE_ERROR] : ({geofenceEvents}: GeofenceEventsState, {error}) => {
    geofenceEvents.loading = false;
    geofenceEvents.acknowledgedError   = error;
  },
  [BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_REQUEST] : ({bulkAcknowledge}: GeofenceEventsState) => {
    bulkAcknowledge.loading = true;
  },
  [BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_RESPONSE] : ({bulkAcknowledge}: GeofenceEventsState, {events}) => {
    bulkAcknowledge.loading = false;
    bulkAcknowledge.result  = events;
  },
  [BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_ERROR] : ({bulkAcknowledge}: GeofenceEventsState, {error}) => {
    bulkAcknowledge.loading = false;
    bulkAcknowledge.error   = error;
  },
  [SET_GEOFENCE_GRID_FILTER_MODEL] : ({grid}: GeofenceEventsState, {model}) => {
    grid.filterModel = model;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: GeofenceEventsState = initialState, action) => {
  reducers[action.type]?.(draft, action);
  return draft;
});
